import { useEffect, useState } from 'react';
import { Drawer, Button, Spin, Empty, Alert, Space } from 'antd';
import { ExclamationCircleOutlined } from '@ant-design/icons';
import { TacheService } from '../../services/TacheService';
import { catchError } from '../../services/DaoService';
import TacheBacklogItem from '../tache/TacheBacklogItem';
import PropTypes from 'prop-types';

/**
 * Drawer de confirmation affiché quand l'utilisateur tente de clôturer un
 * dossier (passage à une étape résolue) alors qu'il reste des tâches non
 * résolues. Rendu en Drawer (pas Modal) pour ne pas entrer en conflit avec
 * le TacheQuickView qui peut s'ouvrir par-dessus en cliquant une tâche.
 */
export default function AffaireCloseConfirmDrawer({ affaireId, visible, onCancel, onConfirm }) {
  const [loading, setLoading] = useState(true);
  const [confirming, setConfirming] = useState(false);
  const [taches, setTaches] = useState([]);

  const load = () => {
    setLoading(true);
    TacheService.getInstance()
      .search({ affaire_id: affaireId, resolu: false })
      .then(r => setTaches(r.data.data ?? r.data))
      .catch(catchError)
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (visible) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, affaireId]);

  const handleTacheUpdate = (updated) => {
    setTaches(prev => updated?.statut?.resolu
      ? prev.filter(t => t.uid !== updated.uid)
      : prev.map(t => (t.uid === updated.uid ? updated : t)));
  };

  const handleConfirm = async () => {
    setConfirming(true);
    try {
      await onConfirm();
    } finally {
      setConfirming(false);
    }
  };

  return (
    <Drawer
      open={visible}
      onClose={onCancel}
      placement="right"
      width={Math.min(520, window.innerWidth - 16)}
      title="Clôturer ce dossier ?"
    >
      <Spin spinning={loading}>
        {taches.length === 0 && !loading ? (
          <Empty description="Aucune tâche non résolue restante" />
        ) : (
          <>
            <Alert
              type="warning"
              showIcon
              icon={<ExclamationCircleOutlined />}
              message={`Il reste ${taches.length} tâche${taches.length !== 1 ? 's' : ''} non résolue${taches.length !== 1 ? 's' : ''} dans ce dossier.`}
              description="Vous pouvez les ouvrir ou les clôturer directement ci-dessous, ou confirmer la clôture du dossier malgré tout."
              style={{ marginBottom: 16 }}
            />
            <div>
              {taches.map(tache => (
                <div key={tache.uid} style={{ borderBottom: '1px solid var(--border-subtle, #F1F2F4)' }}>
                  <TacheBacklogItem tache={tache} onUpdate={handleTacheUpdate} />
                </div>
              ))}
            </div>
          </>
        )}
      </Spin>

      <Space style={{ marginTop: 20, width: '100%', justifyContent: 'flex-end' }}>
        <Button onClick={onCancel}>Annuler</Button>
        <Button type="primary" danger loading={confirming} onClick={handleConfirm}>
          Confirmer la clôture
        </Button>
      </Space>
    </Drawer>
  );
}

AffaireCloseConfirmDrawer.propTypes = {
  affaireId: PropTypes.number,
  visible: PropTypes.bool.isRequired,
  onCancel: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
};
